import { createEffect, DEV } from "solid-js"
import { BufferAttribute, FrontSide, Group, Mesh, MeshLambertMaterial, Scene, Vector3 } from "three"
import { onCleanupRemoveAndDispose } from "../../../../../rx/cleanup/onCleanupRemoveAndDispose"
import { useNodeNeighborBaseFloorElevationReactiveMap } from "../../../../../rx/memo/useNodeNeighborBaseFloorElevationReactiveMap"
import { useTileEdgeListSignal } from "../../../../../rx/signal/useTileEdgeListSignal"
import { DIJKSTRA_DIRECTION_EAST_INDEX, DIJKSTRA_DIRECTION_NORTH_INDEX, DIJKSTRA_DIRECTION_SOUTH_INDEX, DIJKSTRA_DIRECTION_WEST_INDEX } from "../../../../core/direction/dijkstraDirectionCharsLowerCase"
import { Engine } from "../../../../core/engine/Engine.type"
import { ELEVATION_SIZE, RIDGE_ELEVATION_DIFFERENCE } from "../../../../core/entity/EntityElevation/elevation_consts"
import resetEntityRngConfig from "../../../../core/rng/resetEntityRngConfig"
import { globalNoise3D100 } from "../../../../core/rng/SimplexNoise/Global/globalNoise3D"
import tmpRng from "../../../../core/rng/tmpRng"
import range from "../../../../core/util/math/range"
import Unexpected from "../../../../Exception/Unexpected.class"
import isNil from "../../../../ldsh/isNil"
import isNotNil from "../../../../ldsh/isNotNil"
import { VERTICES_PER_TILE } from "../../../consts"
import generateUVs, { UVProjectionPlane } from "../../../fn/generateUVs"
import getTerrainMesh1FromNormalizedTileRXZ from "../../TerrainMesh1V2/getTerrainMesh1FromNormalizedTileRXZ"
import { FLOOR_SEGMENTS } from "../TileFloor/createTerrainMesh1V3TileFloorGroup"
import createSurfaceFromPointsGridGeometry from "./createSurfaceFromPointsGridGeometry"
import { getBaseRidgeColor } from "./getBaseRidgeColor"
import { getSeamlessStoneCliffFaceMountainTexture1600TexturePromise } from "./textures/SeamlessStoneCliffFaceMountainTexture1600"
// import SeamlessStoneCliffFaceMountainTexture1600 from "./textures/SeamlessStoneCliffFaceMountainTexture1600.jpg"
// import SeamlessStoneCliffFaceMountainTexture1600B from "./textures/SeamlessStoneCliffFaceMountainTexture1600B.jpg"

// console.log('SeamlessStoneCliffFaceMountainTexture1600', SeamlessStoneCliffFaceMountainTexture1600B)

const VERTICAL_VERTICES_PER_WORLD_UNIT = 2
// const SEGMENT_STEP = 2 / FLOOR_SEGMENTS
const SEGMENT_STEP = 0.5

const USE_VERTEX_COLORS = true

export default function createTerrainMesh1V3RidgeV3EdgesEffect(
  engine: Engine,
  scene: Scene,
): void {
  // console.log('createTerrainMesh1V3RidgeV3EdgesEffect')
  const [getTileEdgeList] = useTileEdgeListSignal(engine)
  const neighborBaseElevationReactiveMap = useNodeNeighborBaseFloorElevationReactiveMap(engine)
  const group = new Group()
  scene.add(group)
  onCleanupRemoveAndDispose(group)

  const material = new MeshLambertMaterial({
    // color: randomColorInt(tmpRng),
    side: FrontSide,
  })
  if (USE_VERTEX_COLORS) {
    material.vertexColors = true
    // material.side = BackSide
  }
  createEffect(() => {
    material.color.set(getBaseRidgeColor())
    material.needsUpdate = true
  })

  getSeamlessStoneCliffFaceMountainTexture1600TexturePromise().then((texture) => {
    // const scale = 200
    // texture.repeat.set(scale, scale)
    // texture.wrapS = texture.wrapT = ClampToEdgeWrapping
    // texture.generateMipmaps = false
    // texture.minFilter = LinearFilter
    texture.needsUpdate = true
    // texture.colorSpace = SRGBColorSpace
    material.map = texture
    material.bumpMap = texture
    material.needsUpdate = true
  })

  // getSeamlessStoneCliffFaceMountainTexture1600TexturePromise().then((texture) => {
  //   // texture = texture.clone()
  //   texture.needsUpdate = true
  //   // const scale = 1/200
  //   // const scale = 1
  //   // texture.repeat.set(scale, scale)
  //   // texture.
  //   runWithObject3DOwner(group, () => {
  //     createMountedInterval(() => {
  //       // material.map = texture
  //       // material.map = material.map ? null : texture
  //       // material.map = null

  //       texture.needsUpdate = true
  //       material.map = texture
  //       material.needsUpdate = true

  //       material.color.setHex(randomColorInt(tmpRng))
  //       material.needsUpdate = true
  //     }, 1000)
  //   })

  //   setDebugImageList([texture])
  // })

  {
    // noiseMap16Scaled64NormalMapTexturePromise.then((texture) => {
    //   texture.wrapS = texture.wrapT = RepeatWrapping
    //   // resolve(texture)
    //   texture.needsUpdate = true
    //   material.map = texture
    //   material.needsUpdate = true
    //   setDebugImageList([texture])
    //   material.wireframe = true
    // })

    // const texture2 = new TextureLoader().load(SeamlessStoneCliffFaceMountainTexture1600B, (texture2) => {
    //   texture2.wrapS = texture2.wrapT = RepeatWrapping
    //   // resolve(texture)
    //   texture2.needsUpdate = true
    //   material.map = noiseMap16Scaled64NormalMapTexturePromise
    //   material.needsUpdate = true
    //   setDebugImageList([texture2])
    // }, undefined, (error) => {
    //   // reject(error)
    //   console.error(error)
    // })
    // texture2.needsUpdate = true
    // material.map = texture2
    // material.needsUpdate = true
  }

  createEffect(() => {
    const edges = getTileEdgeList()
    // Derive step size from FLOOR_SEGMENTS to match tile floor resolution
    const step = 2 / FLOOR_SEGMENTS
    const normalizedXList = range(-1, 1, step)
    const colCount = normalizedXList.length
    // console.log('normalizedXList', normalizedXList)

    for (let i = 0; i < edges.length; i++) {
      const edge = edges[i]
      createEffect(() => {
        const rdi = edge.rdi()
        if (isNotNil(rdi)) {
          const [highNode] = edge.nodesDesc()
          if (DEV && !highNode) {
            throw new Unexpected('!highNode')
          }
          const nodeIndex = highNode.index
          if (DEV && isNil(nodeIndex)) {
            throw new Unexpected('!isNil(nodeIndex)')
          }
          const seed = edge.l1.id() || 0
          const data: Array<Array<Vector3>> = []
          createEffect(() => {
            resetEntityRngConfig(seed, tmpRng)
            // console.log('effect.elevationDiff')
            const elevationDiff = edge.elevation.diff()
            const isHorizontal = rdi === DIJKSTRA_DIRECTION_NORTH_INDEX || rdi === DIJKSTRA_DIRECTION_SOUTH_INDEX
            if (elevationDiff >= RIDGE_ELEVATION_DIFFERENCE) {
              const baseElevationsCollection = neighborBaseElevationReactiveMap.get(nodeIndex)
              if (baseElevationsCollection) {
                const rowsCount = (((elevationDiff * ELEVATION_SIZE) + 1) * VERTICAL_VERTICES_PER_WORLD_UNIT) | 0
                const verticesCount = rowsCount * colCount
                // console.log('rowsCount', rowsCount)
                // reset points, later we will attempt to re-use them
                data.length = rowsCount
                // normalized y
                const ny = 1
                // top row
                const row0 : Array<Vector3> = data[0] || Array(colCount)
                for (let j = 0; j < colCount; j++) {
                  const nx = normalizedXList[j]
                  row0[j] ||= new Vector3()
                  const position = row0[j]
                  if (rdi === DIJKSTRA_DIRECTION_SOUTH_INDEX) {
                    const wx = (edge.x + (nx / 2)) * VERTICES_PER_TILE
                    const wy = edge.y * VERTICES_PER_TILE
                    const wh = getTerrainMesh1FromNormalizedTileRXZ(baseElevationsCollection, nx, -ny)
                    position.set(wx, wh, wy)
                  } else if (rdi === DIJKSTRA_DIRECTION_EAST_INDEX) {
                    const wx = edge.x * VERTICES_PER_TILE
                    const wy = (edge.y + (nx / 2)) * VERTICES_PER_TILE
                    const wh = getTerrainMesh1FromNormalizedTileRXZ(baseElevationsCollection, ny, -nx)
                    position.set(wx, wh, wy)
                  } else if (rdi === DIJKSTRA_DIRECTION_NORTH_INDEX) {
                    const wx = (edge.x - (nx / 2)) * VERTICES_PER_TILE
                    const wy = edge.y * VERTICES_PER_TILE
                    const wh = getTerrainMesh1FromNormalizedTileRXZ(baseElevationsCollection, -nx, ny)
                    position.set(wx, wh, wy)
                  } else if (rdi === DIJKSTRA_DIRECTION_WEST_INDEX) {
                    const wx = edge.x * VERTICES_PER_TILE
                    const wy = (edge.y - (nx / 2)) * VERTICES_PER_TILE
                    const wh = getTerrainMesh1FromNormalizedTileRXZ(baseElevationsCollection, -ny, nx)
                    position.set(wx, wh, wy)
                  }
                }

                data[0] = row0
                for (let k = 1, prevRow = row0; k < rowsCount; k++) {
                  const rowK : Array<Vector3> = data[k] || Array(colCount)
                  for (let j = 0; j < colCount; j++) {
                    rowK[j] ||= new Vector3()
                    rowK[j].copy(prevRow[j])
                    rowK[j].y -= SEGMENT_STEP
                  }
                  data[k] = rowK
                  prevRow = rowK
                }

                // console.log('data', data)
                const geometry = createSurfaceFromPointsGridGeometry(data, isHorizontal)
                const positionAttr = geometry.attributes.position
                if (DEV && verticesCount !== positionAttr.count) {
                  throw new Unexpected(`verticesCount ${verticesCount} !== positionAttr.count ${positionAttr.count}`)
                }
                if (USE_VERTEX_COLORS) {
                  const colorsCount = verticesCount * 3
                  const colors = new Float32Array(colorsCount)
                  // let value = 0.5
                  // for (let index = 0; index < colorsCount; index++) {
                  //   // colors[index] = nextNumberRange(0.2, 0.8, tmpRng)
                  //   value += 0.03
                  //   colors[index] = value
                  //   if (value >= 1) {
                  //     value = 0
                  //   }
                  // }
                  for (let x = 0; x < colCount; x++) {
                    for (let y = 0; y < rowsCount; y++) {
                      const colorIndex = (y * colCount + x) * 3
                      const position = data[y][x]
                      const noiseInputScale = 0.5
                      const noiseValue = globalNoise3D100(position.x * noiseInputScale, position.y * noiseInputScale, position.z * noiseInputScale)
                      const color0 = (noiseValue * 0.2) + 0.4
                      colors[colorIndex] = color0
                      colors[colorIndex + 1] = color0
                      colors[colorIndex + 2] = color0
                    }
                  }
                  geometry.setAttribute("color", new BufferAttribute(colors, 3))
                }

                if (isHorizontal) {
                  generateUVs(geometry, UVProjectionPlane.XY)
                } else {
                  generateUVs(geometry, UVProjectionPlane.YZ)
                }

                if (DEV) {
                  if (!geometry.attributes.uv) {
                    throw new Unexpected('!geometry.attributes.uv')
                  }
                }

                const mesh = new Mesh(geometry, material)
                group.add(mesh)
                // mesh.position.y += 2
                onCleanupRemoveAndDispose(mesh)
                return
              }
            }
            data.length = 0
          })
        }
      })
    }
  })

  // createMountedInterval(() => {
  //   console.log('group', group.children.length)
  // }, 3000)
}
